import pRetry, { AbortError } from 'p-retry';

import { AssetsMap, fillAssetsMap, FullAssetsMap, AssetsModule } from '../AssetsMap';
import type { AppEntry, CrossOrigin } from '../Def';
import { baseDebugger as debug } from './Debugger';
import { ScriptNode } from '../ScriptNode';
import { StyleNode } from '../StyleNode';

import { reasonableTime } from '../utils/reasonableTime';
import { parseUri } from '../utils/navigateToUrl';
import { toAbsolutePath } from '../utils/url';

type Versionable<T, V> = T & { version: V };

const FETCH_ENTRY_TIMEOUT = 4000;

export async function resolveAssetsFromEntry(
    entry: AppEntry,
    crossOrigin?: CrossOrigin
): Promise<{ scripts: ScriptNode[]; styles: StyleNode[]; isHTML?: boolean; isJSON?: boolean; isJS?: boolean }> {
    debug('Call resolveAssetsFromEntry(%O).', entry);

    entry.url = entry.url.trim();

    let entryPathname: string;
    let entryHref: string;

    try {
        // Based on location.href is not so reasonable, as it always changes.
        const { pathname, href } = new URL(entry.url, location.href);
        entryPathname = pathname.toLowerCase();
        entryHref = href;
    } catch (e) {
        console.warn('Parse entry as URL failed: ', e);

        const { pathname, href } = parseUri(entry.url);
        entryPathname = pathname.toLowerCase();
        entryHref = href;
    }

    if (entryPathname.endsWith('.js')) {
        debug('Treat %s as a JS UMD entry.', entry);
        return {
            isJS: true,
            styles: [],
            scripts: [
                new ScriptNode({
                    src: toAbsolutePath(entry.url),
                    crossOrigin,
                }),
            ],
        };
    }

    if (entryPathname.endsWith('.mjs')) {
        debug('Treat %s as a JS ESM entry.', entry);
        return {
            isJS: true,
            styles: [],
            scripts: [
                new ScriptNode({
                    src: toAbsolutePath(entry.url),
                    type: 'module',
                    crossOrigin,
                }),
            ],
        };
    }

    const timeout = entry.timeout ?? FETCH_ENTRY_TIMEOUT;

    const entryResponse = await pRetry<Response>(
        async () => {
            const response = await reasonableTime<Response>(
                fetch(entry.url, {
                    method: 'GET',
                    cache: 'no-cache',
                    redirect: 'follow',
                    ...entry.requestInit,
                }),
                timeout,
                `Fetching entry from ${entry.url} exceeds %dms.`
            );

            if (response.status >= 300) {
                throw new AbortError(response.statusText);
            }

            return response;
        },
        {
            retries: entry.retries,
        }
    );

    const contentType = (entryResponse.headers.get('Content-Type') || '').toLowerCase();

    switch (true) {
        // JSON entry
        case contentType.includes('application/json'):
        case entryPathname.endsWith('.json'): {
            const exports = (await entryResponse.json()) as
                | Versionable<AssetsMap, 1>
                | Versionable<{ css: string[]; js: string[]; module?: AssetsModule }, 2>;

            debug('Detected a JSON entry %s: %O.', entry, exports);

            let fullAssetsMap: FullAssetsMap;

            if (exports.version === 2) {
                fullAssetsMap = fillAssetsMap({
                    module: exports.module || AssetsModule.UMD,
                    initial: {
                        css: exports.css,
                        js: exports.js,
                    },
                    async: {},
                });
            } else {
                fullAssetsMap = fillAssetsMap({
                    module: exports.module,
                    initial: exports.initial,
                    async: exports.async,
                });
            }

            const isModule = fullAssetsMap.module === AssetsModule.ESM ? 'module' : undefined;

            return {
                isJSON: true,
                styles: fullAssetsMap.initial.css.map(css => new StyleNode({ href: toAbsolutePath(css, entryHref) })),
                scripts: (isModule ? fullAssetsMap.initial.js.slice(-1) : fullAssetsMap.initial.js).map(
                    js =>
                        new ScriptNode({
                            type: isModule,
                            src: toAbsolutePath(js, entryHref),
                            crossOrigin,
                        })
                ),
            };
        }
        // HTML entry
        case contentType.includes('text/html'):
        case contentType.includes('application/xhtml+xml'):
        case /\.(htm|[sx]?html)$/.test(entryPathname): {
            const htmlStr = await entryResponse.text();
            debug('Detected a HTML entry %s: %s.', entry, htmlStr);

            const root = document.implementation.createHTMLDocument().documentElement;
            root.innerHTML = htmlStr;

            const scripts: ScriptNode[] = [];
            const styles: StyleNode[] = [];

            const scriptNodes = Array.from(root.querySelectorAll('script'));
            const styleNodes = Array.from(root.querySelectorAll('link,style'));

            if (debug.enabled) {
                console.debug('<script/> queried:', scriptNodes);
                console.debug('<style(link)/> queried:', styleNodes);
            }

            scriptNodes.forEach(scriptNode => {
                const sn = new ScriptNode({
                    owner: entry.url,
                    src: toAbsolutePath(scriptNode.getAttribute('src') || undefined, entryHref),
                    // Using wholeText instead of innerText is more friendly for jsdom.
                    content: (scriptNode.firstChild as Text)?.wholeText,
                    // HTMLScriptElement.async is always true in new created document.
                    async: scriptNode.hasAttribute('async'),
                    defer: scriptNode.defer,
                    type: scriptNode.type,
                    entry: scriptNode.hasAttribute('entry'),
                    crossOrigin: (scriptNode.crossOrigin as CrossOrigin) || crossOrigin,
                    noModule: scriptNode.noModule ?? scriptNode.hasAttribute('nomodule'),
                });

                debug('Create a ScriptNode: %s.', sn);

                scripts.push(sn);
            });

            styleNodes.forEach(node => {
                let sn: StyleNode;
                if (node.tagName.toUpperCase() === 'LINK') {
                    const linkNode = node as HTMLLinkElement;
                    sn = new StyleNode({
                        owner: entry.url,
                        media: linkNode.media,
                        disabled: linkNode.disabled,
                        href: toAbsolutePath(linkNode.getAttribute('href') ?? undefined, entryHref),
                        type: linkNode.type,
                        rel: linkNode.rel,
                        crossOrigin: (linkNode.crossOrigin as CrossOrigin) ?? crossOrigin,
                    });
                } else {
                    const styleNode = node as HTMLStyleElement;

                    sn = new StyleNode({
                        owner: entry.url,
                        type: styleNode.getAttribute('type') ?? undefined,
                        media: styleNode.media,
                        content: styleNode.innerHTML,
                    });
                }

                debug('Create a StyleNode: %s.', sn);

                styles.push(sn);
            });

            return {
                isHTML: true,
                scripts,
                styles,
            };
        }
        // JS entry
        case contentType.includes('text/javascript'):
        case contentType.includes('application/javascript'):
        case contentType.includes('application/ecmascript'): {
            // FIXME This is a js entry
            try {
                // If it's ESM, create a function will throw.
                Function('', await entryResponse.text());
                debug('Detected a JavaScript UMD entry: %s.', entry);
                return {
                    isJS: true,
                    styles: [],
                    scripts: [
                        new ScriptNode({
                            src: toAbsolutePath(entry.url),
                            crossOrigin,
                        }),
                    ],
                };
            } catch {
                debug('Detected a JavaScript ESM entry: %s.', entry);
                return {
                    isJS: true,
                    styles: [],
                    scripts: [
                        new ScriptNode({
                            type: 'module',
                            src: toAbsolutePath(entry.url),
                            crossOrigin,
                        }),
                    ],
                };
            }
        }
        default:
            throw Error(`Unrecognized entry format: ${entry.url}.`);
    }
}
